import React from "react";

import {
  BrokenLinkIcon as BrokenLink,
  LinkIcon,
  SettingsIcon,
  InfoSmIcon
} from "@packages/ds";
import { useIntl } from "react-intl";

import { IconControl, Link } from "ds/Button";
import Dialog from "ds/Dialog";
import { Tooltip } from "ds/Tooltip";
import { selectEnvironmentDomainsEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { useAppSelector } from "Store/hooks";

import * as S from "./LinkPreview.styles";

type ProjectLinkProps = {
  hasProjectRoutes: boolean;
  isInactive: boolean;
  mainUrl?: string;
  domainsUrl: string;
  projectDefaultBranch: string;
  projectDefaultDomain: string;
  organizationId: string;
  projectId: string;
  environmentId?: never;
  onDomainLinkClick: (e: React.MouseEvent) => void;
  isEnvironment?: false;
};

type EnvironmentLinkProps = {
  hasProjectRoutes: boolean;
  isInactive: false;
  mainUrl?: string;
  domainsUrl: string;
  projectDefaultBranch?: never;
  projectDefaultDomain?: never;
  organizationId: string;
  projectId: string;
  environmentId: string;
  onDomainLinkClick?: (e: React.MouseEvent) => void;
  isEnvironment: true;
};

type LinkPreviewProps = ProjectLinkProps | EnvironmentLinkProps;

const LinkPreview = ({
  hasProjectRoutes,
  isInactive,
  mainUrl,
  domainsUrl,
  projectDefaultBranch,
  projectDefaultDomain,
  organizationId,
  projectId,
  environmentId,
  isEnvironment,
  onDomainLinkClick
}: LinkPreviewProps) => {
  const intl = useIntl();

  const environmentDomainsEnabled = useAppSelector(
    selectEnvironmentDomainsEnabled
  );

  const variant = isEnvironment ? "env" : "main";

  if (!hasProjectRoutes) {
    return (
      <S.EnvUrlBroken variant={variant}>
        <BrokenLink />
        <S.InactiveMessage>
          {intl.formatMessage({
            id:
              variant === "env"
                ? "environment.status.no_routes.title"
                : "environment.status.no_routes.main.title"
          })}
        </S.InactiveMessage>
        <Dialog
          align="start"
          trigger={
            <IconControl aria-label="info-trigger-no-routes">
              <InfoSmIcon />
            </IconControl>
          }
          analyticId="environment.status.no_routes.update"
          title={intl.formatMessage({
            id: "environment.status.inactive.url"
          })}
          description={intl.formatMessage({
            id: "environment.status.no_routes.update"
          })}
          link={
            <Link
              analyticId="learnmore"
              href={intl.formatMessage({
                id: "links.documentation.define_routes"
              })}
            >
              {intl.formatMessage({ id: "learnmore" })}
            </Link>
          }
        />
      </S.EnvUrlBroken>
    );
  }

  if (isInactive) {
    return (
      <S.EnvUrlBroken variant={variant}>
        <BrokenLink />
        <S.InactiveMessage>
          {intl.formatMessage({
            id:
              variant === "env"
                ? "environment.status.inactive.title"
                : "environment.status.inactive.main.title"
          })}
        </S.InactiveMessage>
        <Dialog
          align="start"
          trigger={
            <IconControl aria-label="inactive-environment">
              <InfoSmIcon />
            </IconControl>
          }
          analyticId="environment.status.inactive.update"
          title={intl.formatMessage({
            id: "environment.status.inactive.url"
          })}
          description={intl.formatMessage({
            id: "environment.status.inactive.update"
          })}
          link={
            <S.LinksWrapper>
              <Link
                analyticId="view_settings"
                to={`/${organizationId}/${projectId}/${encodeURIComponent(
                  variant === "main" ? projectDefaultBranch : environmentId!
                )}/settings`}
              >
                {intl.formatMessage({ id: "view_settings" })}
              </Link>
              <Link
                analyticId="learnmore"
                href={intl.formatMessage({
                  id: "links.documentation.inactive_environment"
                })}
              >
                {intl.formatMessage({ id: "learnmore" })}
              </Link>
            </S.LinksWrapper>
          }
        />
      </S.EnvUrlBroken>
    );
  }

  if (!mainUrl) {
    return (
      <S.NoUrl variant={variant}>
        {intl.formatMessage({ id: "project.details.no_url" })}
      </S.NoUrl>
    );
  }

  if (isEnvironment) {
    return (
      <S.EnvUrl variant={variant}>
        <LinkIcon />
        <Tooltip tooltip={mainUrl} analyticId="external_link_preview">
          <S.ExternalEnvLink
            target="_blank"
            href={mainUrl}
            rel="noopener noreferrer"
          >
            {mainUrl}
          </S.ExternalEnvLink>
        </Tooltip>

        <S.DomainLink to={domainsUrl} onClick={onDomainLinkClick}>
          <Tooltip
            tooltip={intl.formatMessage({
              id: `project.details.domain.${
                projectDefaultDomain ? "update" : "set"
              }`
            })}
            analyticId={`project.details.domain.${
              projectDefaultDomain ? "update" : "set"
            }`}
          >
            {environmentDomainsEnabled && (
              <span aria-label="Settings">
                <SettingsIcon />
              </span>
            )}
          </Tooltip>
        </S.DomainLink>
      </S.EnvUrl>
    );
  }

  return (
    <S.MainUrl>
      <S.Truncate>
        <Tooltip tooltip={mainUrl} analyticId="link_preview_mainUrl">
          <a href={mainUrl} rel="noopener noreferrer" target="_blank">
            {mainUrl}
          </a>
        </Tooltip>
      </S.Truncate>
      <S.DomainLink to={domainsUrl} onClick={onDomainLinkClick}>
        <Tooltip
          tooltip={intl.formatMessage({
            id: `project.details.domain.${
              projectDefaultDomain ? "update" : "set"
            }`
          })}
          analyticId={`project.details.domain.${
            projectDefaultDomain ? "update" : "set"
          }`}
        >
          <span aria-label="Settings">
            <SettingsIcon />
          </span>
        </Tooltip>
      </S.DomainLink>
    </S.MainUrl>
  );
};

export default LinkPreview;
